import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Box, TextField, IconButton, useTheme, useMediaQuery, keyframes, styled, Tooltip } from '@mui/material';
import { Mic as MicIcon, Stop as StopIcon, VolumeUp as VolumeUpIcon } from '@mui/icons-material';

// =====================================================
// DECLARAÇÕES DE TIPO PARA SPEECH RECOGNITION
// =====================================================
declare global {
  interface Window {
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

// =====================================================
// ANIMAÇÕES E COMPONENTES ESTILIZADOS
// =====================================================
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const PulsingStopIcon = styled(StopIcon)(({ theme }) => ({
  animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
  color: theme.palette.error.main,
}));

interface FormularioTextoProps {
  procedimentoInicial: string;
  sinteseInicial: string;
  condutaInicial: string;
  observacoesInicial: string;
  onOpenVoiceModal: (field: string) => void;
}

export interface FormularioTextoRef {
  getValues: () => {
    procedimento: string;
    sintese: string;
    conduta: string;
    observacoes: string;
  };
  setValues: (values: {
    procedimento: string;
    sintese: string;
    conduta: string;
    observacoes: string;
  }) => void;
}

// COMPONENTE ISOLADO PARA OS CAMPOS DE TEXTO
const FormularioTexto = forwardRef<FormularioTextoRef, FormularioTextoProps>(({
  procedimentoInicial,
  sinteseInicial,
  condutaInicial,
  observacoesInicial,
  onOpenVoiceModal
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Estados completamente independentes
  const [procedimento, setProcedimento] = useState(procedimentoInicial);
  const [sintese, setSintese] = useState(sinteseInicial);
  const [conduta, setConduta] = useState(condutaInicial);
  const [observacoes, setObservacoes] = useState(observacoesInicial);

  // Estados para reconhecimento de voz direta em cada campo
  const [isListeningProcedimento, setIsListeningProcedimento] = useState(false);
  const [isListeningSintese, setIsListeningSintese] = useState(false);
  const [isListeningConduta, setIsListeningConduta] = useState(false);
  const [isListeningObservacoes, setIsListeningObservacoes] = useState(false);
  
  const recognitionRef = useRef<any>(null);
  const currentFieldRef = useRef<string>('');

  // Sincronizar com props iniciais
  useEffect(() => {
    setProcedimento(procedimentoInicial);
    setSintese(sinteseInicial);
    setConduta(condutaInicial);
    setObservacoes(observacoesInicial);
  }, [procedimentoInicial, sinteseInicial, condutaInicial, observacoesInicial]);

  // Inicializar reconhecimento de voz
  useEffect(() => {
    if (typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      if (recognitionRef.current) {
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'pt-BR';
        
        let baseText = '';
        let finalAccumulated = '';
        
        recognitionRef.current.onstart = () => {
          // Capturar o texto atual do campo ativo quando iniciar o reconhecimento
          const currentField = currentFieldRef.current;
          console.log('🎤 Iniciando reconhecimento para campo:', currentField);
          
          if (currentField === 'procedimento') {
            setProcedimento(prev => {
              baseText = prev;
              finalAccumulated = '';
              return prev;
            });
          } else if (currentField === 'sintese') {
            setSintese(prev => {
              baseText = prev;
              finalAccumulated = '';
              return prev;
            });
          } else if (currentField === 'conduta') {
            setConduta(prev => {
              baseText = prev;
              finalAccumulated = '';
              return prev;
            });
          } else if (currentField === 'observacoes') {
            setObservacoes(prev => {
              baseText = prev;
              finalAccumulated = '';
              return prev;
            });
          }
        };
        
        recognitionRef.current.onresult = (event: any) => {
          let finalTranscript = '';
          let interimTranscript = '';
          
          // Processar todos os resultados
          for (let i = 0; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          
          // Acumular texto final confirmado
          if (finalTranscript) {
            finalAccumulated += finalTranscript;
          }
          
          // Construir texto completo: base + final acumulado + intermediário
          const completeText = baseText + finalAccumulated + interimTranscript;
          
          // Atualizar o campo correspondente
          const currentField = currentFieldRef.current;
          if (currentField === 'procedimento') {
            setProcedimento(completeText);
          } else if (currentField === 'sintese') {
            setSintese(completeText);
          } else if (currentField === 'conduta') {
            setConduta(completeText);
          } else if (currentField === 'observacoes') {
            setObservacoes(completeText);
          }
        };
        
        recognitionRef.current.onerror = (event: any) => {
          console.error('Erro no reconhecimento de voz:', event.error);
          // Parar reconhecimento em todos os campos
          setIsListeningProcedimento(false);
          setIsListeningSintese(false);
          setIsListeningConduta(false);
          setIsListeningObservacoes(false);
        };
        
        recognitionRef.current.onend = () => {
          // Parar reconhecimento em todos os campos
          setIsListeningProcedimento(false);
          setIsListeningSintese(false);
          setIsListeningConduta(false);
          setIsListeningObservacoes(false);
        };
      }
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []); // Remover dependência para evitar recriar o recognition

  // Funções genéricas para controlar o reconhecimento de voz
  const startVoiceRecognition = (fieldName: string) => {
    const isAnyListening = isListeningProcedimento || isListeningSintese || isListeningConduta || isListeningObservacoes;
    
    if (recognitionRef.current && !isAnyListening) {
      try {
        currentFieldRef.current = fieldName;
        recognitionRef.current.start();
        
        // Ativar o estado do campo correspondente
        if (fieldName === 'procedimento') setIsListeningProcedimento(true);
        else if (fieldName === 'sintese') setIsListeningSintese(true);
        else if (fieldName === 'conduta') setIsListeningConduta(true);
        else if (fieldName === 'observacoes') setIsListeningObservacoes(true);
        
        console.log(`🎤 Iniciando reconhecimento de voz para ${fieldName}`);
      } catch (error) {
        console.error('Erro ao iniciar reconhecimento:', error);
      }
    }
  };

  const stopVoiceRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      // Os estados serão atualizados pelo onend
      console.log('🛑 Parando reconhecimento de voz');
    }
  };

  // Funções específicas para cada campo
  const toggleVoiceRecognitionProcedimento = () => {
    if (isListeningProcedimento) {
      stopVoiceRecognition();
    } else {
      startVoiceRecognition('procedimento');
    }
  };

  const toggleVoiceRecognitionSintese = () => {
    if (isListeningSintese) {
      stopVoiceRecognition();
    } else {
      startVoiceRecognition('sintese');
    }
  };

  const toggleVoiceRecognitionConduta = () => {
    if (isListeningConduta) {
      stopVoiceRecognition();
    } else {
      startVoiceRecognition('conduta');
    }
  };

  const toggleVoiceRecognitionObservacoes = () => {
    if (isListeningObservacoes) {
      stopVoiceRecognition();
    } else {
      startVoiceRecognition('observacoes');
    }
  };

  const handleTextToSpeech = (text: string) => {
    // Parar qualquer áudio que esteja tocando
    speechSynthesis.cancel();
    
    if (text.trim()) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'pt-BR';
      
      // Configurar para NÃO repetir quando terminar
      utterance.onend = () => {
        console.log('🔊 Áudio finalizado');
      };
      
      speechSynthesis.speak(utterance);
    }
  };

  // Expor métodos para o componente pai acessar os valores
  useImperativeHandle(ref, () => ({
    getValues: () => ({
      procedimento,
      sintese,
      conduta,
      observacoes
    }),
    setValues: (values) => {
      setProcedimento(values.procedimento);
      setSintese(values.sintese);
      setConduta(values.conduta);
      setObservacoes(values.observacoes);
    }
  }), [procedimento, sintese, conduta, observacoes]);

  return (
    <Box>
      {/* Procedimento */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: isMobile ? 1 : 1, 
        mb: isMobile ? 3 : 2,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {isMobile && (
          <Box sx={{ display: 'flex', gap: 0.25, alignSelf: 'flex-end', mb: '6px' }}>
            <IconButton
              onClick={toggleVoiceRecognitionProcedimento}
              color={isListeningProcedimento ? "error" : "primary"}
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
                ...(isListeningProcedimento && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningProcedimento ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(procedimento)}
              color="primary"
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
        <TextField
          label="Procedimento *"
          multiline
          rows={isMobile ? 4 : 3}
          value={procedimento}
          onChange={(e) => setProcedimento(e.target.value)}
          placeholder="Descreva o procedimento realizado... (ou clique no microfone para ditar)"
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: isMobile ? '16px' : '14px', // Evita zoom no iOS
              ...(isListeningProcedimento && {
                borderColor: theme.palette.error.main,
                borderWidth: 2,
                '&:hover': {
                  borderColor: theme.palette.error.main,
                }
              })
            }
          }}
        />
        {!isMobile && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <IconButton
              onClick={toggleVoiceRecognitionProcedimento}
              color={isListeningProcedimento ? "error" : "primary"}
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
                ...(isListeningProcedimento && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningProcedimento ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(procedimento)}
              color="primary"
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Síntese */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: isMobile ? 1 : 1, 
        mb: isMobile ? 6 : 2,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {isMobile && (
          <Box sx={{ display: 'flex', gap: 0.25, alignSelf: 'flex-end', mb: '6px' }}>
            <IconButton
              onClick={toggleVoiceRecognitionSintese}
              color={isListeningSintese ? "error" : "primary"}
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
                ...(isListeningSintese && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningSintese ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(sintese)}
              color="primary"
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
        <TextField
          label="Síntese *"
          multiline
          rows={isMobile ? 4 : 3}
          value={sintese}
          onChange={(e) => setSintese(e.target.value)}
          placeholder="Resumo da sessão e observações relevantes... (ou clique no microfone para ditar)"
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: isMobile ? '16px' : '14px',
              ...(isListeningSintese && {
                borderColor: theme.palette.error.main,
                borderWidth: 2,
                '&:hover': {
                  borderColor: theme.palette.error.main,
                }
              })
            }
          }}
        />
        {!isMobile && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <IconButton
              onClick={toggleVoiceRecognitionSintese}
              color={isListeningSintese ? "error" : "primary"}
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
                ...(isListeningSintese && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningSintese ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(sintese)}
              color="primary"
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Conduta */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: isMobile ? 1 : 1, 
        mb: isMobile ? 6 : 2,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {isMobile && (
          <Box sx={{ display: 'flex', gap: 0.25, alignSelf: 'flex-end', mb: '6px' }}>
            <IconButton
              onClick={toggleVoiceRecognitionConduta}
              color={isListeningConduta ? "error" : "primary"}
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
                ...(isListeningConduta && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningConduta ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(conduta)}
              color="primary"
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
        <TextField
          label="Conduta *"
          multiline
          rows={isMobile ? 4 : 3}
          value={conduta}
          onChange={(e) => setConduta(e.target.value)}
          placeholder="Conduta adotada e próximos passos... (ou clique no microfone para ditar)"
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: isMobile ? '16px' : '14px',
              ...(isListeningConduta && {
                borderColor: theme.palette.error.main,
                borderWidth: 2,
                '&:hover': {
                  borderColor: theme.palette.error.main,
                }
              })
            }
          }}
        />
        {!isMobile && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <IconButton
              onClick={toggleVoiceRecognitionConduta}
              color={isListeningConduta ? "error" : "primary"}
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
                ...(isListeningConduta && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningConduta ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(conduta)}
              color="primary"
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Observações */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: isMobile ? 1 : 1, 
        mb: isMobile ? 6 : 2,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {isMobile && (
          <Box sx={{ display: 'flex', gap: 0.25, alignSelf: 'flex-end', mb: '6px' }}>
            <IconButton
              onClick={toggleVoiceRecognitionObservacoes}
              color={isListeningObservacoes ? "error" : "primary"}
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
                ...(isListeningObservacoes && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningObservacoes ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(observacoes)}
              color="primary"
              size="large"
              sx={{ 
                minHeight: 48,
                minWidth: 48,
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
        <TextField
          label="Observações"
          multiline
          rows={isMobile ? 3 : 2}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          placeholder="Observações adicionais (opcional)... (ou clique no microfone para ditar)"
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: isMobile ? '16px' : '14px',
              ...(isListeningObservacoes && {
                borderColor: theme.palette.error.main,
                borderWidth: 2,
                '&:hover': {
                  borderColor: theme.palette.error.main,
                }
              })
            }
          }}
        />
        {!isMobile && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <IconButton
              onClick={toggleVoiceRecognitionObservacoes}
              color={isListeningObservacoes ? "error" : "primary"}
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
                ...(isListeningObservacoes && {
                  backgroundColor: `${theme.palette.error.main}15`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.error.main}25`,
                  }
                })
              }}
            >
              {isListeningObservacoes ? <PulsingStopIcon /> : <MicIcon />}
            </IconButton>
            <IconButton
              onClick={() => handleTextToSpeech(observacoes)}
              color="primary"
              size="small"
              sx={{ 
                mt: 1,
                minHeight: 'auto',
                minWidth: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
});

FormularioTexto.displayName = 'FormularioTexto';

export default FormularioTexto;
